import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { Autocomplete, Box, Button, Chip, TextField, Typography } from '@mui/material'
import { renderColorByString } from 'components/DataGrid/renderColorByString'
import React, { useMemo, useState } from 'react'

interface DealSelectorFormProps {
  title: string
  filter?: string
  deals: any[]
  organizations: any[]
  onSubmit: (message: string) => void
  isInteractive: boolean
}

const TERMINAL_STATUSES = new Set(['Ended', 'Cancelled', 'Replaced'])
const MSA_TYPES = new Set(['MSA', 'RMSA', 'MSA - Housing', 'RMSA - Housing'])

function formatDate(d?: string) {
  if (!d) return ''
  const parsed = new Date(d)
  if (isNaN(parsed.getTime())) return d
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const DealSelectorForm: React.FC<DealSelectorFormProps> = ({
  title,
  filter,
  deals,
  organizations,
  onSubmit,
  isInteractive,
}) => {
  const [selectedDeal, setSelectedDeal] = useState<any | null>(null)
  const [submitted, setSubmitted] = useState(false)

  const filteredDeals = useMemo(() => {
    let filtered = deals.filter(d => d?.EntityType === 'deal')

    if (filter === 'active-msa') {
      filtered = filtered.filter(
        d =>
          !TERMINAL_STATUSES.has(d.dealStatus) &&
          MSA_TYPES.has(d.type) &&
          d.stage === 'Deal Won'
      )
    } else if (filter === 'active') {
      filtered = filtered.filter(d => !TERMINAL_STATUSES.has(d.dealStatus))
    }

    return filtered.sort((a, b) => (a?.name || '').localeCompare(b?.name || ''))
  }, [deals, filter])

  const getOrgName = (deal: any) => {
    const org = organizations.find(o => o?.PK === deal?.PK)
    return org?.name || ''
  }

  const handleSubmit = () => {
    if (!selectedDeal) return
    const version = selectedDeal.semanticVersion || 'N/A'
    const message = `I've selected the deal "${selectedDeal.name}" (PK: ${selectedDeal.PK}, SK: ${selectedDeal.SK}, version: ${version}) for amendment.`
    setSubmitted(true)
    onSubmit(message)
  }

  // Read-only / submitted state
  if (!isInteractive || submitted) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          my: 0.5,
          borderRadius: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          maxWidth: 400,
          opacity: 0.8,
        }}
      >
        {submitted && <CheckCircleOutlineIcon sx={{ fontSize: 16, color: 'success.main' }} />}
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
          {submitted && selectedDeal
            ? `Selected: ${selectedDeal.name}`
            : title}
        </Typography>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        px: 1.5,
        py: 1.5,
        my: 0.5,
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        maxWidth: 400,
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
        {title}
      </Typography>

      <Autocomplete
        size="small"
        options={filteredDeals}
        value={selectedDeal}
        onChange={(_, value) => setSelectedDeal(value)}
        getOptionLabel={(option: any) => option?.name || ''}
        isOptionEqualToValue={(option, value) => option?.SK === value?.SK}
        renderOption={(props, option) => {
          const { key, ...rest } = props as any
          const typeColor = renderColorByString(option?.type)
          return (
            <Box
              component="li"
              key={key}
              {...rest}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start !important',
                gap: 0.25,
                py: 0.75,
                px: 1.5,
              }}
            >
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, lineHeight: 1.3 }}>
                {option?.name}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: typeColor }}>
                  {option?.type}
                </Typography>
                {option?.semanticVersion && (
                  <Chip
                    label={`v${option.semanticVersion}`}
                    size="small"
                    sx={{
                      height: 16,
                      fontSize: '0.6rem',
                      fontWeight: 700,
                      fontFamily: 'monospace',
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      '& .MuiChip-label': { px: 0.5 },
                    }}
                  />
                )}
                <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
                  {formatDate(option?.startDate)} – {formatDate(option?.endDate)}
                </Typography>
              </Box>
              {getOrgName(option) && (
                <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
                  {getOrgName(option)}
                </Typography>
              )}
            </Box>
          )
        }}
        renderInput={params => (
          <TextField
            {...params}
            placeholder="Search deals..."
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1.5,
                fontSize: '0.8rem',
                bgcolor: 'background.default',
              },
            }}
          />
        )}
        sx={{ width: '100%' }}
      />

      <Button
        size="small"
        variant="outlined"
        onClick={handleSubmit}
        disabled={!selectedDeal}
        sx={{
          alignSelf: 'flex-end',
          fontWeight: 500,
          fontSize: '0.75rem',
          borderColor: 'text.ai_agent_text',
          color: 'text.ai_agent_text',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'scale(1.05)',
            borderColor: 'text.highlight',
            color: 'text.primary',
          },
        }}
      >
        Select Deal
      </Button>
    </Box>
  )
}

export default DealSelectorForm
